"use client";

import { Anchor, Ship, Waves, Fish } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
export default function Wholesale() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.3
  });
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: pillarsRef,
    isVisible: pillarsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const featureBlocks = [{
    title: "Direct Sourcing",
    description: "Wild-caught seafood harvested by our own vessel, The Miss Yvonne."
  }, {
    title: "Premium Quality",
    description: "Expertly cut and flash-frozen to preserve the taste of the Gulf."
  }, {
    title: "Reliable Supply",
    description: "Consistent delivery schedules for your restaurant or market needs."
  }];
  const pillars = [{
    icon: Ship,
    title: "Heritage Vessel",
    description: "Built in 1965, our boat represents decades of sustainable fishing."
  }, {
    icon: Waves,
    title: "Gulf Coast Fresh",
    description: "Sourced from the right waters with responsible harvesting practices."
  }, {
    icon: Anchor,
    title: "Proven Reliability",
    description: "Built by hand and proven by time, we are your trusted partner."
  }, {
    icon: Fish,
    title: "Expertly Cut",
    description: "Premium fillets prepared with care for your commercial kitchen."
  }];
  return <section data-section-id="3290" id="wholesale-signup-form" className="bg-background text-foreground relative overflow-hidden py-32 md:py-48 lg:py-[200px] px-6 md:px-12 lg:px-20">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-end overflow-hidden" aria-hidden="true">
        <span className="font-serif italic text-[18vw] leading-none whitespace-nowrap select-none" style={{
        color: "transparent",
        WebkitTextStroke: "1px var(--foreground)",
        opacity: 0.08,
        transform: "translateX(8%)"
      }}>
          1965
        </span>
      </div>

      <div className="relative mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {}
          <div className="lg:col-span-4 flex flex-col">
            <div ref={eyebrowRef} className={`transition-all duration-1000 ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <p className="font-mono text-xs uppercase tracking-[0.25em] mb-16" style={{
              color: "var(--primary)"
            }}>
                Commercial Accounts — 03
              </p>
            </div>

            <div className="flex flex-col gap-10">
              {featureBlocks.map((block, i) => <div key={i} data-index={i} className="flex flex-col gap-3">
                  <Separator className="w-12" style={{
                backgroundColor: "var(--secondary)"
              }} />
                  <h3 className="font-default text-xs uppercase tracking-[0.2em] font-medium text-foreground">
                    {block.title}
                  </h3>
                  <p className="font-sans text-sm leading-relaxed text-muted-foreground max-w-xs">
                    {block.description}
                  </p>
                </div>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-8 lg:col-start-5 flex flex-col justify-center">
            <h2 ref={headlineRef} className={`font-default italic font-light text-foreground leading-[0.95] tracking-tight text-5xl md:text-7xl lg:text-[96px] transition-all duration-[1400ms] ease-out ${headlineVisible ? "opacity-100 [clip-path:inset(0_0_0_0)]" : "opacity-0 [clip-path:inset(0_100%_0_0)]"}`}>
              Built by Hand. Proven by Time.
            </h2>

            {}
            <div className="mt-12 flex items-center gap-1 h-16 overflow-hidden">
              {Array.from({
              length: 80
            }).map((_, i) => {
              const heights = [12, 28, 18, 44, 22, 36, 16, 52, 30, 20];
              const h = heights[i % heights.length];
              return <span key={i} data-index={i} className="block w-px" style={{
                height: `${h}px`,
                backgroundColor: "var(--primary)",
                opacity: 0.55,
                animation: `vine-pulse 3.${i % 9}s ease-in-out infinite`,
                animationDelay: `${i % 12 * 0.08}s`
              }} />;
            })}
            </div>

            <p className="font-mono text-[10px] uppercase tracking-[0.3em] mt-6" style={{
            color: "var(--primary)"
          }}>
              30° 24' 42" N / 88° 49' 42" W — Ocean Springs
            </p>
          </div>
        </div>

        {}
        <div ref={pillarsRef} className="mt-32 md:mt-40 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-14 max-w-5xl">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={i} data-index={i} className={`group flex flex-col gap-4 transition-all duration-1000 ${pillarsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <Icon className="size-5" strokeWidth={1.25} style={{
              color: "var(--primary)"
            }} />
                <div className="flex flex-col gap-2">
                  <h4 className="font-default text-xs uppercase tracking-[0.2em] font-medium text-foreground">
                    {pillar.title}
                  </h4>
                  <span className="block h-px w-0 transition-all duration-500 ease-out group-hover:w-16" style={{
                backgroundColor: "var(--primary)"
              }} />
                  <p className="font-sans text-sm leading-relaxed text-muted-foreground max-w-sm">
                    {pillar.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>

      <style>{`
        @keyframes vine-pulse {
          0%, 100% { transform: scaleY(0.6); opacity: 0.4; }
          50% { transform: scaleY(1.2); opacity: 0.8; }
        }
      `}</style>
    </section>;
}